import { useEffect, useRef, useState } from 'react';
import './App.css';
import { galleryImages, profile, projects } from './siteConfig';

const SWAP_DELAY_MS = 520;
const SETTLE_DELAY_MS = 1220;

function App() {
  const [view, setView] = useState('home');
  const [imageIndex, setImageIndex] = useState(0);
  const [projectIndex, setProjectIndex] = useState(0);
  const [isLaunched, setIsLaunched] = useState(false);
  const [isProjectLaunched, setIsProjectLaunched] = useState(false);
  const timersRef = useRef([]);
  const currentImage = galleryImages[imageIndex];
  const currentProject = projects.length > 0 ? projects[projectIndex % projects.length] : null;

  useEffect(() => {
    const activeTimers = timersRef.current;

    return () => {
      activeTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  function launchPortrait() {
    if (isLaunched) {
      return;
    }

    setIsLaunched(true);
    const swapTimer = window.setTimeout(() => {
      setImageIndex((current) => (current + 1) % galleryImages.length);
    }, SWAP_DELAY_MS);
    const settleTimer = window.setTimeout(() => {
      setIsLaunched(false);
    }, SETTLE_DELAY_MS);

    timersRef.current.push(swapTimer, settleTimer);
  }

  function launchProjectTag() {
    if (isProjectLaunched || projects.length <= 1) {
      return;
    }

    setIsProjectLaunched(true);
    const swapTimer = window.setTimeout(() => {
      setProjectIndex((current) => (current + 1) % projects.length);
    }, SWAP_DELAY_MS);
    const settleTimer = window.setTimeout(() => {
      setIsProjectLaunched(false);
    }, SETTLE_DELAY_MS);

    timersRef.current.push(swapTimer, settleTimer);
  }

  return (
    <main
      className={`site-shell ${view === 'projects' ? 'is-projects' : 'is-home'}`}
      data-testid="cinematic-stage"
    >
      <DecorativeField />

      <div className="stage-track" aria-live="polite">
        <section className="scene home-scene" aria-labelledby="home-title">
          <div className="copy-cluster">
            <p className="eyebrow">饭点准时开场</p>
            <h1 id="home-title">{profile.name}</h1>
            <p className="slogan">{profile.slogan}</p>
            <p className="intro">{profile.intro}</p>

            <div className="action-row" aria-label="主要操作">
              <button className="primary-action" onClick={() => setView('projects')}>
                打开项目看板
              </button>
              <div className="social-links" aria-label="社交链接">
                {profile.socialLinks.map((link) => (
                  <a
                    className="social-link"
                    href={link.href}
                    key={link.label}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="tiny-status" aria-label="小档案">
              <span>今日菜单</span>
              <strong>{currentImage.caption}</strong>
            </div>
          </div>

          <div className="hanger-rig" aria-label="吊着的图片轮播">
            <div className="ceiling-line" aria-hidden="true" />
            <div className="try-me-cue" aria-hidden="true">
              <span>点我试试</span>
              <svg viewBox="0 0 170 96" role="presentation">
                <path d="M12 20 C 42 5, 55 54, 84 38 S 121 18, 148 60" />
                <path d="M136 55 L 151 63 L 144 47" />
              </svg>
            </div>
            <button
              aria-label="让吊着的图片飞高换下一张"
              className={`portrait-pendulum ${isLaunched ? 'is-launched' : ''}`}
              onClick={launchPortrait}
              type="button"
            >
              <span className="rope" aria-hidden="true">
                <span />
              </span>
              <span className="portrait-frame">
                <img src={currentImage.src} alt={currentImage.alt} />
              </span>
              <span className="portrait-shadow" aria-hidden="true" />
            </button>
            <span className="orbit-dot dot-one" aria-hidden="true" />
            <span className="orbit-dot dot-two" aria-hidden="true" />
            <span className="orbit-dot dot-three" aria-hidden="true" />
          </div>
        </section>

        <section className="scene projects-scene" aria-labelledby="projects-title">
          <div className="board-copy">
            <p className="eyebrow">下一幕</p>
            <h2 id="projects-title">项目看板</h2>
            <p>灵感会像小票一样贴上来，先留一张干净桌面。</p>
            <button className="secondary-action" onClick={() => setView('home')} type="button">
              回到主页
            </button>
          </div>

          <div className="project-board project-hanger-rig" aria-label="个人项目">
            <div className="project-ceiling-line" aria-hidden="true" />
            <button
              aria-label="点一点切换项目"
              className={`project-pendulum ${isProjectLaunched ? 'is-launched' : ''}`}
              disabled={projects.length === 0}
              onClick={launchProjectTag}
              type="button"
            >
              <span className="project-rope" aria-hidden="true">
                <span />
              </span>
              <span className="project-tag">
                <span className="switch-hint">点一点切换</span>
                {currentProject ? (
                  <>
                    <span className="project-count">
                      {projectIndex + 1} / {projects.length}
                    </span>
                    <span className="project-status">{currentProject.status}</span>
                    <span className="project-title">{currentProject.title}</span>
                    <span className="project-description">{currentProject.description}</span>
                  </>
                ) : (
                  <>
                    <span className="project-status">空桌面</span>
                    <span className="project-title">项目配置暂时留空</span>
                    <span className="project-description">桌面先空着，给下一份作品留位置。</span>
                  </>
                )}
              </span>
              <span className="project-shadow" aria-hidden="true" />
            </button>

            {currentProject?.href ? (
              <a className="project-link" href={currentProject.href} rel="noreferrer" target="_blank">
                查看项目
              </a>
            ) : null}
            <span className="project-spark spark-one" aria-hidden="true" />
            <span className="project-spark spark-two" aria-hidden="true" />
            <span className="project-spark spark-three" aria-hidden="true" />
          </div>
        </section>
      </div>
    </main>
  );
}

function DecorativeField() {
  return (
    <div className="decorative-field" aria-hidden="true">
      <span className="grain grain-one" />
      <span className="grain grain-two" />
      <span className="grain grain-three" />
      <span className="grain grain-four" />
      <span className="steam steam-one" />
      <span className="steam steam-two" />
      <span className="steam steam-three" />
      <span className="bubble bubble-one" />
      <span className="bubble bubble-two" />
      <span className="bubble bubble-three" />
    </div>
  );
}

export default App;
